import React, {useEffect, useRef, useState} from "react";
import {Link, useTranslation} from "gatsby-plugin-react-i18next";
import {CaretDown} from "@phosphor-icons/react";
import {Button} from "../ui/button";
import LanguageSwitcher from "./language-switcher";
import {useNavItems} from "./nav-items";
import chatlabLogo from "../../images/logo-new/chatlab-landscape-tile.svg";

const NavLink = ({item, className = ""}) => (
    item.href
        ? <a href={item.href} className={className}>{item.label}</a>
        : <Link to={item.to} className={className}>{item.label}</Link>
);

const ITEM_CLASS = "text-[15px] leading-snug text-[var(--cl-ink-2)] hover:text-[var(--cl-ink)] hover:underline underline-offset-4";

const ResourceSection = ({title, items}) => (
    <div className="flex flex-col gap-4">
        <span className="cl-eyebrow">{title}</span>
        <ul className="flex flex-col gap-2.5">
            {items.map((item) => (
                <li key={item.to || item.href} className="flex items-baseline gap-2">
                    <NavLink item={item} className={ITEM_CLASS}/>
                    {item.note ? (
                        <span className="text-[12px] text-[var(--cl-muted)] whitespace-nowrap">{item.note}</span>
                    ) : null}
                </li>
            ))}
        </ul>
    </div>
);

const ResourceColumn = ({group}) => (
    <div className="flex flex-col gap-7">
        <ResourceSection title={group.title} items={group.items}/>
        {(group.sections || []).map((section) => (
            <div key={section.title} className="pt-6 border-t border-[var(--cl-rule)]">
                <ResourceSection title={section.title} items={section.items}/>
            </div>
        ))}
    </div>
);

const useHoverMenu = () => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const closeTimer = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        const onClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onClick);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onClick);
        };
    }, [open]);

    const enter = () => { clearTimeout(closeTimer.current); setOpen(true); };
    const leave = () => { closeTimer.current = setTimeout(() => setOpen(false), 150); };

    return {open, setOpen, rootRef, enter, leave};
};

const PrimaryMenu = ({item}) => {
    const {open, setOpen, rootRef, enter, leave} = useHoverMenu();

    return (
        <li ref={rootRef} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
            <Link
                to={item.to}
                partiallyActive
                aria-expanded={open}
                aria-haspopup="true"
                onFocus={() => setOpen(true)}
                className="inline-flex items-center gap-1 py-2 hover:text-[var(--cl-ink)] transition-colors"
                activeClassName="text-[var(--cl-ink)] font-semibold"
            >
                {item.label}
                <CaretDown size={14} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`}/>
            </Link>
            <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 ${open ? "block" : "hidden"}`}>
                <ul className="w-[280px] flex flex-col p-3 bg-[var(--cl-surface)] border border-[var(--cl-rule)] rounded-[4px] shadow-[0_18px_44px_rgba(17,17,20,0.12)]">
                    {item.children.map((child) => (
                        <li key={child.to}>
                            <Link
                                to={child.to}
                                onClick={() => setOpen(false)}
                                className="flex flex-col gap-0.5 px-3 py-2.5 rounded-[4px] hover:bg-[var(--cl-surface-2)] transition-colors"
                                activeClassName="bg-[var(--cl-surface-2)]"
                            >
                                <span className="text-[15px] font-semibold text-[var(--cl-ink)]">{child.label}</span>
                                {child.note ? <span className="text-[12px] text-[var(--cl-muted)]">{child.note}</span> : null}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </li>
    );
};

const NavbarDesktop = () => {
    const {t} = useTranslation();
    const nav = useNavItems();
    const {open, setOpen, rootRef, enter, leave} = useHoverMenu();

    return (
        <header className="sticky top-0 z-30 w-full h-20 bg-[var(--cl-paper)]/85 backdrop-blur-md border-b border-[var(--cl-rule)] nav-desktop">
            <nav className="w-full max-w-screen-2xl mx-auto h-full px-5 flex items-center justify-between gap-8">
                <Link to="/" title={t("ChatLab - Custom ChatGPT for your website")} className="shrink-0">
                    <img src={chatlabLogo} width={150} height={41} className="w-[140px] relative -top-[2px]" alt="ChatLab"/>
                </Link>

                <ul className="flex items-center gap-7 text-[15px] text-[var(--cl-ink-2)]">
                    {nav.primary.map((item) => (
                        item.children ? <PrimaryMenu key={item.to} item={item}/> : (
                            <li key={item.to}>
                                <Link to={item.to} className="py-2 hover:text-[var(--cl-ink)] transition-colors" activeClassName="text-[var(--cl-ink)] font-semibold">
                                    {item.label}
                                </Link>
                            </li>
                        )
                    ))}
                    <li ref={rootRef} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
                        <button
                            type="button"
                            aria-expanded={open}
                            aria-haspopup="true"
                            onClick={() => setOpen(true)}
                            className="inline-flex items-center gap-1 py-2 hover:text-[var(--cl-ink)] transition-colors"
                        >
                            {t("Resources")}
                            <CaretDown size={14} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`}/>
                        </button>
                        <div
                            className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 ${open ? "block" : "hidden"}`}
                        >
                            <div className="w-[760px] grid grid-cols-[minmax(0,0.75fr)_minmax(0,1.28fr)_minmax(0,1.07fr)] gap-x-10 p-8 bg-[var(--cl-surface)] border border-[var(--cl-rule)] rounded-[4px] shadow-[0_18px_44px_rgba(17,17,20,0.12)]">
                                {nav.resources.map((group) => <ResourceColumn key={group.title} group={group}/>)}
                            </div>
                        </div>
                    </li>
                </ul>

                <div className="flex items-center gap-5 shrink-0">
                    <LanguageSwitcher/>
                    <a href={nav.signIn} className="text-[15px] text-[var(--cl-ink-2)] hover:text-[var(--cl-ink)]">
                        {t("Sign in")}
                    </a>
                    <Button variant="primary" size="sm" asChild>
                        <a href={nav.signUp} className="px-6 text-[14px]">{t("Sign up")}</a>
                    </Button>
                </div>
            </nav>
        </header>
    );
};

export default NavbarDesktop;
